import { Box, Table, Sheet, Chip } from "@mui/joy";
import DarkTypography from "../../common/DarkTypography";
import MotifCell from "./MotifCell";
import LeaveValidationRequestActions from "./LeaveValidationRequestActions";
import LeaveValidationRequestsMobile from "./LeaveValidationRequestsMobile";
import type { LeaveRequest, LeaveType } from "../../../api/leave/Leave.typed";
import {
  formatLeaveDurationLabel,
  formatLeavePeriod,
  getStatusChip,
} from "../../../utils/leaveHelpers";
import { shouldDisplayLeaveMotifForUi } from "../../../utils/planningLeaveHelpers";
import { hrCompactTableSx, hrSheetSx } from "../../../styles/globalStyles";
import useAppSettings from "../../../contexts/app/appSettings";

export type LeaveValidationRequestsTableProps = {
  requests: LeaveRequest[];
  leaveTypes: LeaveType[];
  getUserName: (userId: number) => string;
  formatDate: (dateStr: string) => string;
  onApprove: (requestId: number) => void;
  onReject: (requestId: number) => void;
  onCancel: (requestId: number) => void;
  onApproveCancellation: (requestId: number) => void;
  onRejectCancellation: (requestId: number) => void;
  onOpenReason: (text: string) => void;
  canValidate?: boolean;
};

export default function LeaveValidationRequestsTable(props: LeaveValidationRequestsTableProps) {
  const {
    requests,
    leaveTypes,
    getUserName,
    formatDate,
    onApprove,
    onReject,
    onCancel,
    onApproveCancellation,
    onRejectCancellation,
    onOpenReason,
    canValidate = true,
  } = props;
  const { config } = useAppSettings();
  const companyTimeZone = config?.TimeZone;
  const motifVisible = (request: LeaveRequest) => shouldDisplayLeaveMotifForUi(request, leaveTypes);

  const renderStatusChip = (status: number) => {
    const info = getStatusChip(status);
    if (!info) return null;

    return (
      <Chip variant="soft" color={info.color} size="sm">
        {info.name}
      </Chip>
    );
  };

  return (
    <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
      <Sheet
        sx={{
          ...hrSheetSx,
          borderRadius: "6px",
          overflowX: "auto",
          display: { xs: "none", md: "block" },
          maxWidth: "100%",
        }}
      >
        <Table hoverRow size="sm" sx={hrCompactTableSx}>
          <thead>
            <tr>
              <th style={{ width: 140 }}>Demandeur</th>
              <th style={{ width: 160 }}>Type</th>
              <th style={{ width: 270 }}>Période</th>
              <th style={{ width: 90 }}>Durée</th>
              <th>Motif</th>
              <th style={{ width: 140 }}>Statut</th>
              <th style={{ width: 110 }}>Demandé</th>
              <th style={{ width: 110 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request.id}>
                <td>
                  <DarkTypography level="body-sm" sx={{ fontWeight: "bold" }}>
                    {getUserName(request.user_id) || `Utilisateur #${request.user_id}`}
                  </DarkTypography>
                </td>
                <td>
                  <DarkTypography level="body-sm">
                    {leaveTypes.find((t) => t.ID === request.type_id)?.Name || "—"}
                  </DarkTypography>
                </td>
                <td>
                  <DarkTypography level="body-sm">
                    {formatLeavePeriod(request, companyTimeZone)}
                  </DarkTypography>
                </td>
                <td>
                  <DarkTypography level="body-sm" sx={{ fontWeight: "bold" }}>
                    {formatLeaveDurationLabel(request, companyTimeZone)}
                  </DarkTypography>
                </td>
                <td>
                  <MotifCell
                    text={motifVisible(request) ? request.reason || "—" : "—"}
                    hasReason={motifVisible(request) && !!request.reason}
                    onOpen={() => onOpenReason(request.reason ?? "")}
                  />
                </td>
                <td>{renderStatusChip(request.status)}</td>
                <td>
                  <DarkTypography level="body-sm">{formatDate(request.created_at)}</DarkTypography>
                </td>
                <td>
                  <LeaveValidationRequestActions
                    request={request}
                    canValidate={canValidate}
                    onApprove={onApprove}
                    onReject={onReject}
                    onCancel={onCancel}
                    onApproveCancellation={onApproveCancellation}
                    onRejectCancellation={onRejectCancellation}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>

      <LeaveValidationRequestsMobile {...props} />
    </Box>
  );
}
